import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import Card from "components/card";
import Pagination from "components/pagination/Pagination";
import Loader from "assets/img/loader.gif";
import ProductCategoriesService from "services/productCategories.service";
import VendorService from "services/vendor.service";
import CustomProductsService from "services/customProducts.service";
import EventService from "services/events.service";
import Toast from "components/toaster";
import { FiSearch, FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { AiOutlineEdit, AiFillEdit } from "react-icons/ai";
import { MdDeleteForever } from "react-icons/md";
import MapPicker from "views/admin/MapPicker";
import Select from "react-select";
import imageCompression from "browser-image-compression";
import tabCache from "utils/tabCache";

const VendorProducts = () => {
  // My Vendors state - initialize from cache if available
  const [categories, setCategories] = useState(tabCache.vendorProductsCategories.data || []);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoadCategories, setFirstLoadCategories] = useState(tabCache.vendorProductsCategories.firstLoad);
  const [firstLoadProducts, setFirstLoadProducts] = useState(tabCache.vendorProductsProducts.firstLoad);
  const [error, setError] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Upload state
  const [profileUploading, setProfileUploading] = useState(false);
  const [bgUploading, setBgUploading] = useState(false);

  // Map Picker
  const [showMap, setShowMap] = useState(false);
  const [marker, setMarker] = useState(null);

  // Custom Products state - initialize from cache if available
  const [productData, setProductData] = useState(tabCache.vendorProductsProducts.data || []);
  const [isProductLoading, setIsProductLoading] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedProductCategory, setSelectedProductCategory] = useState("");
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [editProductDialogOpen, setEditProductDialogOpen] = useState(false);
  const [viewProductDialogOpen, setViewProductDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productCategories, setProductCategories] = useState([]);
  const [selectedImage, setSelectedImage] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [popupOpenDelete, setPopupOpenDelete] = useState(false);
  const [confirmLoader, setConfirmLoader] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const deleteRef = useRef(null);
  const [selectedCategoryForProduct, setSelectedCategoryForProduct] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Form state for vendors
  const initialFormData = useMemo(() => ({
    vendor_id: "",
    profile_image: "",
    background_images: [],
    vendor_name: "",
    address: "",
    latitude: "",
    longitude: "",
    mobile_number: "",
    email: "",
    website_url: "",
    description: "",
    social_media: {
      youtube: "",
      instagram: "",
      facebook: "",
      twitter: "",
      tiktok: "",
    },
    category_id: "",
    is_active: true,
  }), []);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state for products
  const [productFormData, setProductFormData] = useState({
    image: "",
    title: "",
    price: "",
    discount_percentage: "",
    category_id: "",
    product_url: "",
  });
  const [productErrors, setProductErrors] = useState({});
  const [isProductSubmitting, setIsProductSubmitting] = useState(false);

  // Category filter options for products
  const categoryOptions = [
    { label: "All Categories", value: "" },
    ...productCategories.map(category => ({
      label: category.category_name,
      value: category._id
    }))
  ];

  const colourStyles = {
    option: (styles) => {
      return {
        ...styles,
        backgroundColor: "rgb(30 30 32 / 1) !important",
        ":hover": {
          backgroundColor: "rgba(255, 255, 255, 0.30) !important",
          color: "#ffffff",
        },
      };
    },
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setMarker(null);
    setSelectedCategory(null);
    setIsEditing(false);
  };

  const getCategories = async () => {
    const isFirstLoad = tabCache.vendorProductsCategories.firstLoad;
    
    // Show loader only on first load
    if (isFirstLoad) {
      setIsLoading(true);
    }
    setError("");
    try {
      const response = await ProductCategoriesService.getAllProductCategories({
        is_active: true,
        is_custom: true,
      });
      const all = response?.data?.data?.categories || [];
      const custom = all.filter((c) => c.is_custom === true);
      
      // Update state and cache
      setCategories(custom);
      tabCache.vendorProductsCategories.data = custom;
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.vendorProductsCategories.firstLoad = false;
        setFirstLoadCategories(false);
      }
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to fetch categories");
      const emptyData = [];
      setCategories(emptyData);
      tabCache.vendorProductsCategories.data = emptyData;
      if (isFirstLoad) {
        tabCache.vendorProductsCategories.firstLoad = false;
        setFirstLoadCategories(false);
      }
    } finally {
      if (isFirstLoad) {
        setIsLoading(false);
      }
    }
  };

  const getProductCategories = async () => {
    try {
      const response = await ProductCategoriesService.getAllProductCategories({
        is_active: true,
        is_custom: true,
      });
      const allCategories = response.data.data.categories || [];
      const customCategories = allCategories.filter(category => category.is_custom === true);
      setProductCategories(customCategories);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const prevSearchTextRef = useRef("");
  const getCustomProducts = async (isSearching = false) => {
    const isFirstLoad = tabCache.vendorProductsProducts.firstLoad;
    
    // Show loader on first load or when searching
    if (isFirstLoad || isSearching) {
      setIsProductLoading(true);
    }
    
    try {
      // Use selected vendor's category if a vendor is selected, otherwise use dropdown filter
      const categoryFilter = selectedVendor ? selectedVendor._id : selectedProductCategory;
      
      const response = await CustomProductsService.getAllCustomProducts({
        page: productPage,
        pageSize,
        searchtext: searchText,
        category_id: categoryFilter,
        is_active: true,
      });
      const productsData = response.data.data.products || [];
      
      // Update state and cache
      setProductData(productsData);
      tabCache.vendorProductsProducts.data = productsData;
      setTotalPages(response.data.data.pagination?.pages || 1);
      
      // After first API call, don't show loader next time
      if (isFirstLoad) {
        tabCache.vendorProductsProducts.firstLoad = false;
        setFirstLoadProducts(false);
      }
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to fetch custom products");
      const emptyData = [];
      setProductData(emptyData);
      tabCache.vendorProductsProducts.data = emptyData;
      if (isFirstLoad) {
        tabCache.vendorProductsProducts.firstLoad = false;
        setFirstLoadProducts(false);
      }
    } finally {
      if (isFirstLoad || isSearching) {
        setIsProductLoading(false);
      }
    }
  };

  useEffect(() => {
    // If we have cached data, show it immediately and fetch in background
    if (tabCache.vendorProductsCategories.data) {
      // Data already shown from initial state, just fetch in background
      getCategories();
    } else {
      // No cached data, fetch and show loader
      getCategories();
    }
    getProductCategories();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Check if searchText changed (this indicates a search operation)
    const isSearchChange = prevSearchTextRef.current !== searchText;
    if (isSearchChange) {
      prevSearchTextRef.current = searchText;
    }
    
    // Always fetch when filters change, but show loader on search change if we have cached data
    const isSearching = isSearchChange && tabCache.vendorProductsProducts.data;
    getCustomProducts(isSearching);
    // eslint-disable-next-line
  }, [productPage, pageSize, searchText, selectedProductCategory, selectedVendor]);

  // Debounce search
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
    }, 500),
    []
  );

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    debouncedSearch(value);
  };

  // Click outside handler for delete popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupOpenDelete && deleteRef.current && !deleteRef.current.contains(event.target)) {
        setPopupOpenDelete(false);
        setProductToDelete(null);
        document.body.style.overflow = "unset";
      }
    };

    if (popupOpenDelete) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupOpenDelete]);

  // Vendor functions
  const handleVendorSelect = (category, event) => {
    // Only allow selection if vendor exists
    if (category.is_vendor_exist) {
      const isDeselecting = selectedVendor?._id === category._id;
      setSelectedVendor(isDeselecting ? null : category);
      setProductPage(1); // Reset to first page when vendor changes
      setSelectedProductCategory(""); // Clear dropdown filter when vendor is selected
      
      // Remove focus when deselecting
      if (isDeselecting && event?.currentTarget) {
        event.currentTarget.blur();
      }
    }
  };

  const handleVendorKeyDown = (e, category) => {
    if (category.is_vendor_exist && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleVendorSelect(category, e);
    }
  };

  const openAddForCategory = (category) => {
    setSelectedCategory(category);
    setIsEditing(false);
    setFormData({
      ...initialFormData,
      category_id: category._id,
    });
    setModalOpen(true);
  };

  const openEditForCategory = (category) => {
    setSelectedCategory(category);
    setIsEditing(true);
    const v = category.vendor_profile || {};
    const coords = Array.isArray(v.location?.coordinates) ? v.location.coordinates : [];
    const lngFromLoc = typeof coords[0] === "number" ? coords[0] : "";
    const latFromLoc = typeof coords[1] === "number" ? coords[1] : "";
    const lat = v.latitude ?? latFromLoc ?? "";
    const lng = v.longitude ?? lngFromLoc ?? "";
    setFormData({
      vendor_id: v._id || "",
      profile_image: v.profile_image || "",
      background_images: Array.isArray(v.background_images) ? v.background_images.slice(0, 1) : (v.background_images ? [v.background_images] : []),
      vendor_name: v.vendor_name || "",
      address: v.address || "",
      latitude: lat,
      longitude: lng,
      mobile_number: v.mobile_number || "",
      email: v.email || "",
      website_url: v.website_url || "",
      description: v.description || "",
      social_media: {
        youtube: v.social_media?.youtube || "",
        instagram: v.social_media?.instagram || "",
        facebook: v.social_media?.facebook || "",
        twitter: v.social_media?.twitter || "",
        tiktok: v.social_media?.tiktok || "",
      },
      category_id: category._id,
      is_active: true,
    });
    setMarker(lat && lng ? { lat: Number(lat), lng: Number(lng) } : null);
    setModalOpen(true);
  };

  const handleAddProductForCategory = (category) => {
    setSelectedCategoryForProduct(category);
    setProductFormData({
      image: "",
      title: "",
      price: "",
      discount_percentage: "",
      category_id: category._id,
      product_url: "",
    });
    setSelectedImage("");
    setProductErrors({});
    setAddProductDialogOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.profile_image) newErrors.profile_image = "Profile image is required";

    if (!formData.background_images || formData.background_images.length === 0)
      newErrors.background_images = "One background image is required";

    if (!formData.vendor_name.trim()) newErrors.vendor_name = "Vendor name is required";

    if (!formData.category_id) newErrors.category_id = "Category is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSocialChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      social_media: { ...prev.social_media, [name]: value },
    }));
  };

  const pickLocation = () => {
    setShowMap(true);
  };

  const handleMapClose = () => {
    setShowMap(false);
  };

  const fakeFormik = {
    setFieldValue: (path, value) => {
      if (path === "location.coordinates" && Array.isArray(value) && value.length === 2) {
        const [lng, lat] = value;
        setFormData((prev) => ({
          ...prev,
          latitude: lat,
          longitude: lng,
        }));
      } else if (path === "address") {
        setFormData((prev) => ({ ...prev, address: value }));
      }
    },
  };

  const uploadImage = async (file, setter, key) => {
    if (!file) return;
    setter(true);
    try {
      const form = new FormData();
      form.append("files", file);
      const res = await EventService.fileUpload(form);
      const url = res?.data?.files?.[0];
      if (!url) throw new Error("Upload failed");
      setFormData((prev) => {
        if (key === "profile_image") return { ...prev, profile_image: url };
        if (key === "background_images") return { ...prev, background_images: [url] };
        return prev;
      });
    } catch (e) {
      Toast("error", e?.response?.data?.message || "Image upload failed");
    } finally {
      setter(false);
    }
  };

  const onProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    uploadImage(file, setProfileUploading, "profile_image");
    e.target.value = "";
  };

  const onBgImageChange = (e) => {
    const file = e.target.files?.[0];
    uploadImage(file, setBgUploading, "background_images");
    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const payload = {
        profile_image: formData.profile_image,
        background_images: formData.background_images,
        vendor_name: formData.vendor_name,
        website_url: formData.website_url,
        description: formData.description,
        social_media: formData.social_media,
        category_id: formData.category_id,
        is_active: true,
      };

      if (formData.address && formData.address.trim()) {
        payload.address = formData.address;
      }
      if (formData.latitude !== "" && formData.longitude !== "") {
        const latNum = Number(formData.latitude);
        const lngNum = Number(formData.longitude);
        if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
          payload.latitude = latNum;
          payload.longitude = lngNum;
        }
      }
      if (formData.mobile_number && formData.mobile_number.trim()) {
        payload.mobile_number = formData.mobile_number.trim();
      }
      if (formData.email && formData.email.trim()) {
        payload.email = formData.email.trim();
      }

      if (isEditing) {
        await VendorService.editVendor({ vendor_id: formData.vendor_id, ...payload });
        Toast("success", "Vendor updated successfully");
      } else {
        await VendorService.addVendor(payload);
        Toast("success", "Vendor added successfully");
      }

      setModalOpen(false);
      resetForm();
      getCategories();
      // Refresh products if the updated vendor was selected
      if (selectedVendor && formData.category_id === selectedVendor._id) {
        getCustomProducts();
      }
    } catch (err) {
      Toast("error", err?.response?.data?.message || "Failed to save vendor");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Product functions
  const handlePageChange = (newPage) => {
    setProductPage(newPage);
  };

  const handleCategoryChange = (option) => {
    setSelectedProductCategory(option.value);
    setProductPage(1);
    setSelectedVendor(null); // Clear vendor selection when using dropdown filter
  };

  const handleAddProduct = () => {
    setProductFormData({
      image: "",
      title: "",
      price: "",
      discount_percentage: "",
      category_id: "",
      product_url: "",
    });
    setSelectedImage("");
    setSelectedCategoryForProduct(null);
    setProductErrors({});
    setAddProductDialogOpen(true);
  };

  const handleEditProduct = (product) => {
    setProductFormData({
      image: product.image || "",
      title: product.title || "",
      price: product.price || "",
      discount_percentage: product.discount_percentage || "",
      category_id: product.category_id || "",
      product_url: product.product_url || "",
    });
    setSelectedImage(product.image || "");
    setSelectedProduct(product);
    setProductErrors({});
    setEditProductDialogOpen(true);
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setViewProductDialogOpen(true);
  };

  const closeProductDialog = () => {
    setAddProductDialogOpen(false);
    setEditProductDialogOpen(false);
    setViewProductDialogOpen(false);
    setSelectedProduct(null);
    setProductFormData({
      image: "",
      title: "",
      price: "",
      discount_percentage: "",
      category_id: "",
      product_url: "",
    });
    setSelectedImage("");
    setSelectedCategoryForProduct(null);
    setProductErrors({});
  };

  const validateProductForm = () => {
    const newErrors = {};

    if (!productFormData.image.trim()) {
      newErrors.image = "Image is required";
    }

    if (!productFormData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!productFormData.price || productFormData.price <= 0) {
      newErrors.price = "Price is required and must be greater than 0";
    }

    if (productFormData.discount_percentage && (productFormData.discount_percentage < 0 || productFormData.discount_percentage > 100)) {
      newErrors.discount_percentage = "Discount percentage must be between 0 and 100";
    }

    if (!productFormData.category_id) {
      newErrors.category_id = "Category is required";
    }

    if (productFormData.product_url && !isValidUrl(productFormData.product_url)) {
      newErrors.product_url = "Please enter a valid URL";
    }

    setProductErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!validateProductForm()) return;

    setIsProductSubmitting(true);
    try {
      const submitData = {
        ...productFormData,
        price: parseFloat(productFormData.price),
        discount_percentage: productFormData.discount_percentage ? parseFloat(productFormData.discount_percentage) : 0,
      };

      if (editProductDialogOpen && selectedProduct) {
        await CustomProductsService.updateCustomProduct(selectedProduct._id, submitData);
        Toast("success", "Product updated successfully");
      } else {
        await CustomProductsService.addCustomProduct(submitData);
        Toast("success", "Product added successfully");
      }

      closeProductDialog();
      getCustomProducts();
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to save product");
    } finally {
      setIsProductSubmitting(false);
    }
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product);
    setPopupOpenDelete(true);
    document.body.style.overflow = "hidden";
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    
    setConfirmLoader(true);
    try {
      await CustomProductsService.deleteCustomProduct(productToDelete._id);
      Toast("success", "Product deleted successfully");
      getCustomProducts();
      setPopupOpenDelete(false);
      setProductToDelete(null);
      document.body.style.overflow = "unset";
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Failed to delete product");
    } finally {
      setConfirmLoader(false);
    }
  };

  const handleProductInputChange = (e) => {
    const { name, value } = e.target;
    setProductFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (productErrors[name]) {
      setProductErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageUploading(true);
    setProductErrors(prev => ({ ...prev, image: "" }));

    try {
      const options = {
        maxSizeMB: 0.8,
        maxWidthOrHeight: 1000,
        useWebWorker: true,
        initialQuality: 0.8,
      };

      const compressedFile = await imageCompression(file, options);

      const formData = new FormData();
      formData.append("files", compressedFile);
      const response = await EventService.fileUpload(formData);

      const imageUrl = response?.data?.files?.[0];
      setSelectedImage(imageUrl);
      setProductFormData(prev => ({ ...prev, image: imageUrl }));
    } catch (error) {
      Toast("error", error?.response?.data?.message || "Image upload failed. Please try again.");
    } finally {
      setImageUploading(false);
    }

    e.target.value = "";
  };

  return (
    <div className="-mt-1.5 grid h-full grid-cols-1 gap-5">
      {/* My Vendors Section */}
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full px-6 py-6"}>
          <h2 className="text-xl font-bold text-white mb-4">My Vendors</h2>
          {isLoading ? (
            <div className="py-10 text-center">
              <img src={Loader} alt="Loading..." className="mx-auto h-16 w-16" />
            </div>
          ) : error ? (
            <div className="py-10 text-center text-red-400">{error}</div>
          ) : categories.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-gray-300">No custom categories found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-6 px-6">
              <div className="flex gap-2 sm:gap-5 min-w-max">
                {categories.map((cat, index) => (
                <div 
                  key={cat._id} 
                  className={`rounded-xl bg-navy-800 p-4 border transition-all w-[calc(100vw-48px)] max-w-[calc(100vw-48px)] sm:w-auto sm:max-w-none sm:min-w-[300px] flex-shrink-0 ${
                    index === 1 ? "sm:min-w-[380px]" : ""
                  } ${
                    selectedVendor?._id === cat._id && cat.is_vendor_exist
                      ? "border-blue-500/40 border-2"
                      : "border border-white/10"
                  } ${
                    cat.is_vendor_exist 
                      ? "cursor-pointer hover:border-white/20"
                      : ""
                  } ${
                    cat.is_vendor_exist && selectedVendor?._id !== cat._id
                      ? "focus:outline-none focus:border-blue-500/40 focus:border-2"
                      : ""
                  }`}
                  onClick={(e) => cat.is_vendor_exist && handleVendorSelect(cat, e)}
                  onKeyDown={(e) => handleVendorKeyDown(e, cat)}
                  tabIndex={cat.is_vendor_exist ? 0 : -1}
                  role={cat.is_vendor_exist ? "button" : undefined}
                  aria-label={cat.is_vendor_exist ? `Select vendor ${cat.category_name}` : undefined}
                >
                  <div className="flex flex-col gap-3 mb-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-3">
                      {cat.category_image ? (
                        <img
                          src={cat.category_image}
                          alt={cat.category_name}
                          className="h-10 w-10 rounded-md object-cover border border-white/10"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-md bg-white/10" />
                      )}
                      <div>
                        <div className="text-white font-semibold">{cat.category_name}</div>
                        <div className="text-xs text-gray-600">{cat.description || ""}</div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 w-full md:w-auto md:flex-row md:items-center">
                      {cat.is_vendor_exist ? (
                        <>
                          <button
                            className="flex items-center justify-center gap-2 rounded-md bg-[#FF80001A]/10 text-[#FF8000]  px-3 py-2  hover:bg-orange-400/20 w-full md:w-auto"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditForCategory(cat);
                            }}
                          >
                            <FiEdit className="h-4 w-4" /> Edit
                          </button>
                          <button
                            className="flex items-center justify-center gap-2 rounded-md bg-[#369FFF1A]/10 text-[#369FFF]  px-2 py-2  hover:bg-blue-500/20 whitespace-nowrap w-full md:w-auto"
                           onClick={(e) => {
                              e.stopPropagation();
                              handleAddProductForCategory(cat);
                            }}
                            title="Add Custom Product"
                          >
                            <FiPlus className="h-4 w-4" /> 
                            <span className="-ml-2">Add Product</span>
                          </button>
                        </>
                      ) : (
                        <button
                          className="flex items-center justify-center gap-2 rounded-md bg-orangePrimary px-3 py-2 text-white hover:bg-orangePrimary/20 w-full md:w-auto"
                          onClick={(e) => {
                            e.stopPropagation();
                            openAddForCategory(cat);
                          }}
                        >
                          <FiPlus className="h-4 w-4" /> Add Vendor
                        </button>
                      )}
                    </div>
                  </div>

                  {cat.is_vendor_exist && cat.vendor_profile ? (
                    <div className="bg-navy-800/70 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 border border-white/10 hover:border-white/20 hover:bg-navy-800/90 transition-all duration-300 flex items-center justify-between gap-2 shadow-md hover:shadow-lg hover:scale-[1.02] mt-2">
                      <div className="flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0 max-w-[calc(100%-80px)] sm:max-w-[calc(100%-90px)]">
                        <img
                          src={cat.vendor_profile.profile_image}
                          alt={cat.vendor_profile.vendor_name}
                          className="h-6 w-6 rounded-lg object-cover border border-gray-700 flex-shrink-0"
                        />
                        <span className="text-white text-xs sm:text-sm font-medium truncate">
                          {cat.vendor_profile.vendor_name}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-white/20 p-3 text-center text-gray-600 mt-2">
                      No vendor profile added yet. Click "Add Vendor" to create one.
                    </div>
                  )}
                </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Custom Products Section */}
      <div className="col-span-1 h-full w-full rounded-xl 2xl:col-span-1">
        <Card extra={"w-full h-full px-6 pb-6 overflow-x-auto"}>
          <div className="mt-4 mb-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 -ml-2">
            {/* Left side - Showing products for */}
            {selectedVendor && (
              <div className="px-4 py-2 bg-[#FF80001A]/10  rounded-lg">
                <p className="text-sm text-white">
                  Showing products for: <span className="font-bold text-[#FF8000] ">{selectedVendor.category_name}</span>
                  <button
                    onClick={() => setSelectedVendor(null)}
                    className="ml-2 text-[#FF8000]    underline text-xs"
                  >
                    Clear filter
                  </button>
                </p>
              </div>
            )}
            {/* Right side - Search */}
            <div className="flex items-center w-full sm:w-auto sm:justify-end -mr-3">
              <div className="flex items-center rounded-full bg-navy-900 py-3 text-white w-full sm:max-w-none  xl:w-[180px]">
                <p className="pl-3 pr-2 text-xl">
                  <FiSearch className="h-4 w-4 text-white" />
                </p>
                <input
                  type="text"
                  placeholder="Search..."
                  className="block w-full bg-navy-900 text-sm font-medium text-white outline-none max-w-[120px] placeholder:!text-white"
                  onChange={handleSearchInputChange}
                  value={searchInputValue}
                />
              </div>
            </div>
          </div>
          <div
            className={`table-scroll -mt-2 relative ${
              isProductLoading
                ? "h-[65vh] overflow-hidden"
                : "overflow-x-auto"
            }`}
          >
            {isProductLoading && (
              <div className="pointer-events-none absolute inset-0 z-40 hidden items-center justify-center md:flex">
                <img src={Loader} alt="Loading..." className="h-16 w-16" />
              </div>
            )}
            <table
              className={`w-full ${isProductLoading ? "table-fixed" : ""}`}
              style={
                isProductLoading
                  ? { tableLayout: "fixed", width: "100%" }
                  : { tableLayout: "auto", minWidth: "100%" }
              }
            >
              <thead className={isProductLoading ? "hidden" : ""}>
                <tr className="!border-px !border-gray-400">
                  <th className="w-[70px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Number</p>
                    </div>
                  </th>
                  <th className="w-[120px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Image</p>
                    </div>
                  </th>
                  <th className="w-[200px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Title</p>
                    </div>
                  </th>
                  <th className="w-[120px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Price</p>
                    </div>
                  </th>
                  <th className="w-[120px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Discount</p>
                    </div>
                  </th>
                  <th className="w-[150px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Category</p>
                    </div>
                  </th>
                  <th className="w-[200px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Product URL</p>
                    </div>
                  </th>
                  <th className="w-[120px] cursor-pointer border-b-[1px] border-[#fbfbfb1a] pb-2 pr-4 pt-4 text-start">
                    <div className="items-center justify-between text-xs text-gray-200">
                      <p className="text-sm font-bold uppercase text-white">Actions</p>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {isProductLoading ? (
                  <tr className="md:hidden">
                    <td colSpan={8} className="py-6">
                      <div className="flex justify-center">
                        <img src={Loader} alt="Loading..." className="h-16 w-16" />
                      </div>
                    </td>
                  </tr>
                ) : productData && productData.length > 0 ? (
                  productData.map((row, idx) => (
                    <tr key={row._id} className="group border-b border-[#fbfbfb1a] last:border-none hover:bg-[#8E8E8E]/5 hover:!border-b-0 transition-colors cursor-pointer">
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <p className="text-sm text-white">{(productPage - 1) * pageSize + idx + 1}</p>
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <img
                          src={row.image}
                          alt={row.title}
                          className="h-8 w-8 rounded-lg object-cover "
                        />
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <p className="text-sm font-bold text-white">{row.title}</p>
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <p className="text-sm text-white">${row.price}</p>
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <p className="text-sm text-white">{row.discount_percentage || 0}%</p>
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <p className="text-sm text-white">{row.category_name || '--'}</p>
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        {row.product_url ? (
                          <a 
                            href={row.product_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-orangePrimary underline truncate block max-w-[180px]"
                          >
                            {row.product_url}
                          </a>
                        ) : (
                          <p className="text-sm text-white pl-8">--</p>
                        )}
                      </td>
                      <td className="min-w-[150px] border-white/0 py-3 pr-4">
                        <div className="flex gap-3">
                          <div
                            className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#FF80001A]/10 text-[#FF8000] duration-150"
                            onClick={() => handleEditProduct(row)}
                            title="Edit"
                          >
                            <AiFillEdit />
                          </div>
                          <div
                            className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[#369FFF1A]/10 text-[#369FFF] duration-150"
                            onClick={() => handleViewProduct(row)}
                            title="View"
                          >
                            <IoEye />
                          </div>
                          <div
                            className="ms-center flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg bg-[rgba(255,37,37,0.1)] text-[rgba(255,44,57,1)] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                            onClick={() => handleDeleteProduct(row)}
                            title="Delete"
                          >
                            <MdDeleteForever size={20} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-4 text-center">
                      <p className="my-3">No products found</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {productData && productData.length > 0 && !isProductLoading && (
            <Pagination page={productPage} totalPages={totalPages} handlePageChange={handlePageChange} />
          )}
        </Card>
      </div>

      {/* Vendor Modal */}
      {(modalOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-3xl rounded-xl bg-navy-800 p-6 shadow-lg relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeModal}
            >
              &times;
            </button>

            <h2 className="text-2xl font-bold text-white mb-6">
              {isEditing ? "Edit Vendor" : "Add Vendor"} {selectedCategory ? `- ${selectedCategory.category_name}` : ""}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Profile Image */}
              <div>
                <label className="block text-sm font-bold text-white mb-2">
                  Profile Image <span className="text-red-500">*</span>
                </label>
                {profileUploading ? (
                  <div className="flex items-center justify-center w-full h-28 bg-navy-700 border border-gray-600 rounded-md">
                    <div className="flex items-center space-x-2">
                      <img src={Loader} alt="Uploading..." className="h-6 w-6" />
                      <span className="text-white">Uploading image...</span>
                    </div>
                  </div>
                ) : formData.profile_image ? (
                  <div className="flex justify-start mb-3">
                    <label htmlFor="vendor-profile-upload" className="relative flex h-28 w-28 cursor-pointer justify-center">
                      <img
                        src={formData.profile_image.includes("api.car-hangout.com") || formData.profile_image.includes("hangoutdev.s3.amazonaws.com") ? formData.profile_image : `https://api.car-hangout.com${formData.profile_image}`}
                        className="h-28 w-28 cursor-pointer rounded-lg object-cover border border-gray-600"
                        alt="Vendor profile"
                      />
                      <AiOutlineEdit className="absolute bottom-2 right-2 rounded-full bg-orangePrimary p-1" size={20} />
                    </label>
                  </div>
                ) : (
                  <label htmlFor="vendor-profile-upload" className="flex h-28 w-full cursor-pointer items-center justify-center border border-dashed border-gray-600 duration-300 hover:bg-navy-700 rounded-md">
                    <span className="text-white">Choose Profile Image</span>
                  </label>
                )}
                <input id="vendor-profile-upload" type="file" accept="image/*" onChange={onProfileImageChange} className="hidden" />
                {errors.profile_image && <p className="text-red-500 text-sm mt-1">{errors.profile_image}</p>}
              </div>

              {/* Background Image */}
              <div>
                <label className="block text-sm font-bold text-white mb-2">
                  Background Image <span className="text-red-500">*</span>
                </label>
                {bgUploading ? (
                  <div className="flex items-center justify-center w-full h-28 bg-navy-700 border border-gray-600 rounded-md">
                    <div className="flex items-center space-x-2">
                      <img src={Loader} alt="Uploading..." className="h-6 w-6" />
                      <span className="text-white">Uploading image...</span>
                    </div>
                  </div>
                ) : formData.background_images[0] ? (
                  <div className="flex justify-start mb-3">
                    <label htmlFor="vendor-bg-upload" className="relative flex h-28 w-full max-w-xs cursor-pointer justify-center">
                      <img
                        src={formData.background_images[0].includes("api.car-hangout.com") || formData.background_images[0].includes("hangoutdev.s3.amazonaws.com") ? formData.background_images[0] : `https://api.car-hangout.com${formData.background_images[0]}`}
                        className="h-28 w-full max-w-xs cursor-pointer rounded-lg object-cover border border-gray-600"
                        alt="Background"
                      />
                      <AiOutlineEdit className="absolute bottom-2 right-2 rounded-full bg-orangePrimary p-1" size={20} />
                    </label>
                  </div>
                ) : (
                  <label htmlFor="vendor-bg-upload" className="flex h-28 w-full cursor-pointer items-center justify-center border border-dashed border-gray-600 duration-300 hover:bg-navy-700 rounded-md">
                    <span className="text-white">Choose Background Image</span>
                  </label>
                )}
                <input id="vendor-bg-upload" type="file" accept="image/*" onChange={onBgImageChange} className="hidden" />
                {errors.background_images && <p className="text-red-500 text-sm mt-1">{errors.background_images}</p>}
              </div>

              {/* Basic fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-white mb-2">Vendor Name <span className="text-red-500">*</span></label>
                  <input type="text" name="vendor_name" value={formData.vendor_name} onChange={handleInputChange} placeholder="Enter vendor name" className={`w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary ${errors.vendor_name ? "border-red-500" : "border-gray-600"}`} />
                  {errors.vendor_name && <p className="text-red-500 text-sm mt-1">{errors.vendor_name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-bold text-white mb-2">Mobile</label>
                  <input type="text" name="mobile_number" value={formData.mobile_number} onChange={handleInputChange} placeholder="Enter mobile number" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-white mb-2">Email</label>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter email" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                </div>

                <div>
                  <label className="block text-sm font-bold text-white mb-2">Website</label>
                  <input type="url" name="website_url" value={formData.website_url} onChange={handleInputChange} placeholder="https://" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                </div>
                

                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-white mb-2">Description</label>
                  <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Describe the vendor" className="w-full h-24 px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-bold text-white mb-2">Address & Location</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder="Address will be filled from map" className={`md:col-span-2 w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary ${errors.address ? "border-red-500" : "border-gray-600"}`} />
                  <button type="button" onClick={pickLocation} className="rounded-md bg-orangePrimary px-4 py-2 text-white hover:bg-orangePrimary/80">Pick on Map</button>
                </div>
                <div className="mt-2 text-xs text-gray-600">Lat: {formData.latitude || "-"} • Lng: {formData.longitude || "-"}</div>
                
              </div>

              {/* Socials */}
              <div>
                <label className="block text-sm font-bold text-white mb-2">Social Media (optional)</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input type="url" name="youtube" value={formData.social_media.youtube} onChange={handleSocialChange} placeholder="YouTube URL" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                  <input type="url" name="instagram" value={formData.social_media.instagram} onChange={handleSocialChange} placeholder="Instagram URL" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                  <input type="url" name="facebook" value={formData.social_media.facebook} onChange={handleSocialChange} placeholder="Facebook URL" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                  <input type="url" name="twitter" value={formData.social_media.twitter} onChange={handleSocialChange} placeholder="Twitter URL" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                  <input type="url" name="tiktok" value={formData.social_media.tiktok} onChange={handleSocialChange} placeholder="TikTok URL" className="w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary border-gray-600" />
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button type="button" onClick={closeModal} className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-orangePrimary text-white rounded-md hover:bg-orangePrimary/80 disabled:opacity-50">
                  {isSubmitting ? "Saving..." : isEditing ? "Update Vendor" : "Add Vendor"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Product Dialog */}
      {(addProductDialogOpen || editProductDialogOpen) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-navy-800 p-6 shadow-lg relative max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeProductDialog}
            >
              &times;
            </button>
            <h2 className="text-2xl font-bold text-white mb-6">
              {editProductDialogOpen ? "Edit Product" : "Add New Product"}
              {selectedCategoryForProduct && ` - ${selectedCategoryForProduct.category_name}`}
            </h2>
            
            <form onSubmit={handleProductSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Image */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-white mb-2">
                    Image <span className="text-red-500">*</span>
                  </label>
                  
                  {imageUploading ? (
                    <div className="flex items-center justify-center w-full h-32 bg-navy-700 border border-gray-600 rounded-md">
                      <div className="flex items-center space-x-2">
                        <img src={Loader} alt="Uploading..." className="h-6 w-6" />
                        <span className="text-white">Uploading image...</span>
                      </div>
                    </div>
                  ) : selectedImage ? (
                    <div className="flex justify-center mb-3">
                      <label
                        htmlFor="image-upload"
                        className="relative flex h-32 w-32 cursor-pointer justify-center"
                      >
                        <img
                          src={
                            selectedImage.includes("api.car-hangout.com") ||
                            selectedImage.includes("hangoutdev.s3.amazonaws.com")
                              ? selectedImage
                              : `https://api.car-hangout.com${selectedImage}`
                          }
                          className="h-32 w-32 cursor-pointer rounded-lg object-cover border border-gray-600"
                          alt="Product preview"
                        />
                        <AiOutlineEdit
                          className="absolute bottom-2 right-2 rounded-full bg-orangePrimary p-1"
                          size={20}
                        />
                      </label>
                    </div>
                  ) : (
                    <label
                      htmlFor="image-upload"
                      className="flex h-32 w-full cursor-pointer items-center justify-center border border-dashed border-gray-600 duration-300 hover:bg-navy-700 rounded-md"
                    >
                      <span className="text-white">Choose Product Image</span>
                    </label>
                  )}
                  
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  
                  {productErrors.image && <p className="text-red-500 text-sm mt-1">{productErrors.image}</p>}
                </div>

                {/* Title */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-white mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={productFormData.title}
                    onChange={handleProductInputChange}
                    placeholder="Enter product title"
                    className={`w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary ${
                      productErrors.title ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  {productErrors.title && <p className="text-red-500 text-sm mt-1">{productErrors.title}</p>}
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-bold text-white mb-2">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={productFormData.price}
                    onChange={handleProductInputChange}
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                    className={`w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary ${
                      productErrors.price ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  {productErrors.price && <p className="text-red-500 text-sm mt-1">{productErrors.price}</p>}
                </div>

                {/* Discount Percentage */}
                <div>
                  <label className="block text-sm font-bold text-white mb-2">
                    Discount Percentage
                  </label>
                  <input
                    type="number"
                    name="discount_percentage"
                    value={productFormData.discount_percentage}
                    onChange={handleProductInputChange}
                    placeholder="Enter discount %"
                    min="0"
                    max="100"
                    step="0.01"
                    className={`w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary ${
                      productErrors.discount_percentage ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  {productErrors.discount_percentage && <p className="text-red-500 text-sm mt-1">{productErrors.discount_percentage}</p>}
                </div>

                {/* Category */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-white mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category_id"
                    value={productFormData.category_id}
                    onChange={handleProductInputChange}
                    className={`w-full px-3 py-2 bg-navy-700 border rounded-md text-white focus:outline-none focus:border-orangePrimary ${
                      productErrors.category_id ? "border-red-500" : "border-gray-600"
                    }`}
                  >
                    <option value="">Select a category</option>
                    {productCategories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                  {productErrors.category_id && <p className="text-red-500 text-sm mt-1">{productErrors.category_id}</p>}
                </div>

                {/* Product URL */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-white mb-2">
                    Product URL
                  </label>
                  <input
                    type="url"
                    name="product_url"
                    value={productFormData.product_url}
                    onChange={handleProductInputChange}
                    placeholder="Enter product URL"
                    className={`w-full px-3 py-2 bg-navy-700 border rounded-md text-white placeholder-gray-400 focus:outline-none focus:border-orangePrimary ${
                      productErrors.product_url ? "border-red-500" : "border-gray-600"
                    }`}
                  />
                  {productErrors.product_url && <p className="text-red-500 text-sm mt-1">{productErrors.product_url}</p>}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeProductDialog}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProductSubmitting}
                  className="px-4 py-2 bg-orangePrimary text-white rounded-md hover:bg-orangePrimary/80 transition-colors disabled:opacity-50"
                >
                  {isProductSubmitting ? "Saving..." : editProductDialogOpen ? "Update Product" : "Add Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Product Dialog */}
      {viewProductDialogOpen && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="w-full max-w-lg rounded-xl bg-navy-800 p-6 shadow-lg relative">
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold hover:text-orangePrimary"
              onClick={closeProductDialog}
            >
              &times;
            </button>
            <div className="flex flex-col items-center mb-6">
              <img
                src={selectedProduct.image}
                alt={selectedProduct.title}
                className="h-24 w-24 rounded-full object-cover border-4 border-gray-700 mb-3"
              />
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-2xl font-bold text-white">{selectedProduct.title}</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <span className="font-bold text-white">Price:</span>
                <div className="text-gray-300">${selectedProduct.price}</div>
              </div>
              <div>
                <span className="font-bold text-white">Discount:</span>
                <div className="text-gray-300">{selectedProduct.discount_percentage || 0}%</div>
              </div>
              <div>
                <span className="font-bold text-white">Category:</span>
                <div className="text-gray-300">{selectedProduct.category_name || '--'}</div>
              </div>
              <div>
                <span className="font-bold text-white">Created At:</span>
                <div className="text-gray-300">{selectedProduct.createdAt ? new Date(selectedProduct.createdAt).toLocaleString() : '--'}</div>
              </div>
              <div className="sm:col-span-2">
                <span className="font-bold text-white">Product URL:</span>
                <div className="text-gray-300 break-all">
                  {selectedProduct.product_url ? (
                    <a href={selectedProduct.product_url} target="_blank" rel="noopener noreferrer" className="text-orangePrimary underline">{selectedProduct.product_url}</a>
                  ) : '--'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {popupOpenDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div
            className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg"
            ref={deleteRef}
          >
            <h2 className="mb-4 text-xl font-bold text-white">Delete Product</h2>
            <p className="mb-6 text-gray-600">
              Are you sure you want to delete "{productToDelete?.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setPopupOpenDelete(false);
                  setProductToDelete(null);
                  document.body.style.overflow = "unset";
                }}
                className="px-5 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all duration-200 font-semibold text-sm border border-white/20"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="w-24 rounded-md bg-orangePrimary px-4 py-2 text-white hover:opacity-90"
              >
                {confirmLoader ? (
                  <img
                    src={Loader}
                    alt="Loading..."
                    className="mx-auto h-6 w-6"
                  />
                ) : (
                  "Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMap && (
        <MapPicker
          formik={fakeFormik}
          setShowMap={setShowMap}
          marker={marker}
          setMarker={setMarker}
          handleMapClose={handleMapClose}
        />
      )}
    </div>
  );
};

export default VendorProducts;

